import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsManageUser from '../../../hooks/grid/gridOptions/useGridOptionsManageUser';
import commonColDef from '../../../data/grid/commonColDef';
import useColdefManageUser from '../../../hooks/grid/coldef/useColdefManageUser';
import ActionsManageUser from '../../cellRenderers/ActionsManageUser';
import OrganizationUserCellRenderer from '../../../components/cellRenderers/OrganizationUserCellRenderer';
import UserNameFromEmailCellRenderer from '../../../components/cellRenderers/UserNameFromEmailCellRenderer';
import TradingCompanyCellRenderer from '../../cellRenderers/TradingCompanyCellRenderer';
import SteelMakerCellRenderer from '../../cellRenderers/SteelMakerCellRenderer';
import BusinessTypeCellRenderer from '../../cellRenderers/BusinessTypeCellRenderer';
import SteelCategoryCellRenderer from '../../cellRenderers/SteelCategoryCellRenderer';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import useUserRoles from '../../../hooks/useUserRoles';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { setDialogMode, openDialog, selectUser } from '../../../redux/slices/user';
import getRole from '../../../utils/getRole';
import { EDIT_ACTION, ROLE } from '../../../constants';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import type { Role } from '../../../types/common';
import type { UserWithOrganizationType } from '../../../types/user';

const GridUser = React.forwardRef<AgGridReactType>((props, ref): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { isLoading, users } = useSelector((state: RootState) => state.user);
  const { organizationBase, tradingCompany, steelMaker, businessType, steelCategory } = useSelector((state: RootState) => state.options);
  const { isMaintenanceadmin, isAdminOrPoweruser } = useUserRoles();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsManageUser();
  const customColDef: ColDef = {
    cellClass: 'cell',
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const columnDefs: ColDef[] = useColdefManageUser(t);
  const frameworkComponents = {
    actionsRenderer: ActionsManageUser,
    organizationUserCellRenderer: OrganizationUserCellRenderer,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
    tradingCompanyCellRenderer: TradingCompanyCellRenderer,
    steelMakerCellRenderer: SteelMakerCellRenderer,
    businessTypeCellRenderer: BusinessTypeCellRenderer,
    steelCategoryCellRenderer: SteelCategoryCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (users?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, users]);

  /**
   * カラム幅調整
   */
  useEffect(() => {
    if (gridApi && gridColumnApi && users) {
      const columnKeys: string[] = ['role', 'username', 'email'];
      gridColumnApi.autoSizeColumns(columnKeys);
      gridApi.redrawRows();
    }
  }, [gridColumnApi, users]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    if (isMaintenanceadmin || isAdminOrPoweruser) {
      const data: UserWithOrganizationType = event.data;
      const role: Role = getRole(data.roles);
      // 保守アドミンは編集できない
      if (role !== ROLE.MAINTENANCE_ADMIN) {
        dispatch(selectUser(data));
        dispatch(setDialogMode(isMaintenanceadmin ? EDIT_ACTION.UPDATE : EDIT_ACTION.VIEW));
        dispatch(openDialog());
      }
    }
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div className="ag-theme-balham" style={{ height: '70vh' }}>
        <AgGridReact
          ref={ref}
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={users}
          onGridReady={onGridReady}
          onRowDoubleClicked={onRowDoubleClicked}
          frameworkComponents={frameworkComponents}
          // filterValueGetterで使うデータ
          context={{
            currentLang,
            // reduxにあるものはコピーを渡す
            organizationBase: organizationBase?.map((base) => structuredClone(base)),
            tradingCompany: tradingCompany?.map((tradingCompany) => structuredClone(tradingCompany)),
            steelMaker: steelMaker?.map((maker) => structuredClone(maker)),
            businessType: businessType?.map((business) => structuredClone(business)),
            steelCategory: steelCategory?.map((category) => structuredClone(category)),
            users: users?.map((user) => structuredClone(user)),
          }}
        />
      </div>
    </>
  );
});

// forwardRefの場合にはlinterに要求される場合がある
// https://stackoverflow.com/questions/67992894/component-definition-is-missing-display-name-for-forwardref
GridUser.displayName = 'GridUser';
export default GridUser;
